import { QueryClient } from "@tanstack/react-query";
import {
  getCustomerQueryOptions,
  getTransferPolicyQueryOptions,
  getAdviceQueryOptions,
  getCalculationQueryOptions,
} from "../hooks/api/";
import {
  useUpdateCustomerData,
  getStoredCredentials,
  CustomerCredentials,
} from "../hooks/ui/customerDataStore";

type NavigateFn = (opts: {
  to: string;
  search?: Record<string, string> | undefined;
}) => void;

type InvalidateFn = () => void;

// Begin prefetching customer data as soon as credentials are available, so data is warming in the cache while other UI work happens.
export function prefetchCustomerData(
  credentials: CustomerCredentials,
  queryClient: QueryClient,
) {
  queryClient.prefetchQuery(getCustomerQueryOptions(credentials));
  queryClient.prefetchQuery(getTransferPolicyQueryOptions(credentials));
  queryClient.prefetchQuery(getAdviceQueryOptions(credentials));
}

// Fetches customer, transfer, and advice data, populates the customer store, and navigates to the correct page based on customer status.
export function navigateByCustomerStatus({
  credentials,
  queryClient,
  navigate,
  invalidate,
  searchParams,
}: {
  credentials: CustomerCredentials;
  queryClient: QueryClient;
  navigate: NavigateFn;
  invalidate: InvalidateFn;
  searchParams?: Record<string, string>;
}) {
  Promise.all([
    queryClient.ensureQueryData(getCustomerQueryOptions(credentials)),
    queryClient.ensureQueryData(getTransferPolicyQueryOptions(credentials)),
    queryClient.ensureQueryData(getAdviceQueryOptions(credentials)),
  ])
    .then(([customer, transfer, advice]) => {
      const { setCustomerData } = useUpdateCustomerData.getState();
      const customerData = customer[0];

      setCustomerData({
        email: customerData.user?.email || "",
        phoneNumber: customerData.user?.phone || "",
        firstName: customerData.user?.firstName || "",
        lastName: customerData.user?.lastName || "",
        personalNumber: customerData.user?.ssn || "",
        name: `${customerData.user?.firstName} ${customerData.user?.lastName}`,
        transactionId: customerData.transactionId || "",
        validFrom: customerData.validFrom || "",
        validTo: customerData.validTo || null,
        poa: customerData.poa || {},
        booking: customerData.booking || {},
        credentials: getStoredCredentials(),
        hasLockedAdvice: false,
      });

      if (customer.length === 0) {
        navigate({ to: "/login", search: searchParams });
        return;
      }

      if (transfer?.length > 0) {
        if (transfer.some((t: { status: string }) => t.status === "FINISHED")) {
          queryClient.prefetchQuery(
            getCalculationQueryOptions(credentials, "CUSTOMER_DASHBOARD"),
          );
          invalidate();
          navigate({ to: "/dashboard", search: searchParams });
          return;
        }
        invalidate();
        navigate({ to: "/transfer-status", search: searchParams });
        return;
      }

      if (advice?.hasAgreedAdvice && advice?.insuranceAdvices?.length > 0) {
        queryClient.prefetchQuery(getCalculationQueryOptions(credentials));
        invalidate();
        navigate({ to: "/advice-summary", search: searchParams });
        return;
      }

      invalidate();
      navigate({ to: "/home", search: searchParams });
    })
    .catch(() => {
      invalidate();
      navigate({ to: "/home", search: searchParams });
    });
}
